// Ajustes > Meus dados (0.72, texto 10 C8): o arquivo LEGÍVEL dos dados, organizado por aba, em PDF.
// Cada aba vira um título; cada lista vira uma tabela "Item · Data · Detalhes".
// O arquivo técnico (para restaurar) continua sendo o do store/backup.ts; este aqui é só para ler e guardar.
// Nada sigiloso entra: campos do cofre (🔒), o conteúdo do Diário (cifrado) e os dados internos do app ficam de fora.
import { allDocs } from '../store/store';
import { ABAS, aba } from '../components/theme';
import { DEFS } from '../modulos/defs';
import type { ModId } from '../store/types';
import type { Colecao } from '../modulos/tipos';
import { brDateTime, brYmd, today } from './date';
import { t } from './i18n';
import { gerarECompartilharPdf, type BlocoPdf } from './pdf';

/** Dados internos (não são de uma aba) ou que não se leem: ficam de fora do arquivo legível. */
const FORA = new Set(['ajustes', 'organizar', 'envios', 'figurinhas', 'diariop', 'diario', 'escritorio', 'lixeira']);
/** Campos que nunca entram (cifrados, técnicos ou repetidos). */
const CAMPOS_FORA = new Set(['id', 'segredo', 'cofre', 'chave', 'sal', 'iter', 'rec', 'did', 'e', 'gcal', 'tz', 'color', 'emojiManual', 'ordem', 'por', 'de', 'oc', 'g',
  'pai', 'col', 'alt', 'updatedAt', 'subDone', 'skip', 'done', 'alerts', 'recur', 'pauses', 'link', 'tracos', 'blocos', 'anexos', 'arquivos', 'uri', 'url', 'dado', 'aparelho']);
const TITULO = ['title', 'titulo', 'nome', 'name', 'texto', 'text', 'q', 'descricao', 'desc'];
const DATA = ['start', 'data', 'date', 'venc', 'dia', 'inicio', 'quando', 'createdAt', 'criado', 'criado_em', 'em'];

/** Nomes amigáveis das listas das abas que não são fichas (as fichas usam o nome da coleção). */
const LISTAS: Record<string, string> = {
  tasks: 'Tarefas', quick: 'Lista rápida', boxes: 'Caixas', rooms: 'Cômodos', tarefas: 'Tarefas', ficharios: 'Fichários', materias: 'Matérias',
  paginas: 'Páginas', sessoes: 'Sessões de estudo', prontas: 'Sessões salvas', refeicoes: 'Refeições', cuidados: 'Cuidados', treinos: 'Treinos',
  suplementos: 'Suplementos', items: 'Itens', itens: 'Itens', profiles: 'Perfis', hits: 'Publicações encontradas', pastas: 'Pastas', docs: 'Documentos',
  contas: 'Contas', lancamentos: 'Lançamentos', cartoes: 'Cartões', metas: 'Metas', categorias: 'Categorias', subtasks: 'Subtarefas',
};

const curto = (s: string, n = 300) => (s.length > n ? s.slice(0, n - 1) + '…' : s);
const ehData = (v: unknown) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v);
function dataTela(v: unknown) {
  if (!ehData(v)) return '';
  const s = v as string;
  try { return s.length > 10 ? brDateTime(s) : brYmd(s); } catch { return s; }
}
/** Valor simples em texto (listas viram "a, b"; objetos e textos ricos ficam de fora). */
function valorTexto(v: unknown): string {
  if (v == null || v === '') return '';
  if (typeof v === 'boolean') return v ? t('Sim') : '';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return ehData(v) ? dataTela(v) : curto(v.replace(/\s+/g, ' ').trim());
  if (Array.isArray(v) && v.every(x => typeof x === 'string' || typeof x === 'number')) return curto(v.join(', '));
  return '';
}
const tituloDe = (o: Record<string, any>) => { for (const k of TITULO) if (typeof o[k] === 'string' && o[k].trim()) return curto(o[k].trim(), 160); return ''; };
const dataDe = (o: Record<string, any>) => { for (const k of DATA) if (ehData(o[k])) return dataTela(o[k]); return ''; };

/** Tabelas de uma aba "comum" (não feita de fichas): procura as listas de objetos dentro do documento. */
function listasDoDoc(doc: any): { nome: string; linhas: string[][] }[] {
  const out: { nome: string; linhas: string[][] }[] = [];
  const andar = (v: any, chave: string, prof: number) => {
    if (!v || typeof v !== 'object' || prof > 4) return;
    if (Array.isArray(v)) {
      const objs = v.filter(x => x && typeof x === 'object' && !Array.isArray(x));
      if (!objs.length) return;
      const linhas = objs.map(o => {
        const det = Object.entries(o)
          .filter(([k, x]) => !CAMPOS_FORA.has(k) && !TITULO.includes(k) && !DATA.includes(k) && valorTexto(x))
          .slice(0, 6).map(([k, x]) => `${k}: ${valorTexto(x)}`).join(' · ');
        return [tituloDe(o) || '—', dataDe(o), det];
      }).filter(l => l[0] !== '—' || l[2]);
      if (linhas.length) out.push({ nome: t(LISTAS[chave] ?? chave), linhas });
      objs.forEach(o => Object.entries(o).forEach(([k, x]) => { if (Array.isArray(x) && !CAMPOS_FORA.has(k) && k !== 'subtasks') andar(x, k, prof + 1); }));
      return;
    }
    for (const [k, x] of Object.entries(v)) if (!CAMPOS_FORA.has(k)) andar(x, k, prof + 1);
  };
  andar(doc, '', 0);
  return out;
}

/** Tabelas de uma aba de fichas: uma por coleção, com os nomes dos campos como aparecem no app. */
function listasDasFichas(id: ModId, doc: any): { nome: string; linhas: string[][] }[] {
  const def = DEFS[id];
  const regs: any[] = Array.isArray(doc?.regs) ? doc.regs : [];
  if (!def || !regs.length) return [];
  return def.colecoes.map((col: Colecao) => {
    const daqui = regs.filter(r => r.col === col.id);
    const visiveis = col.campos.filter(c => c.t !== 'cofre' && c.t !== 'ref');
    const principal = visiveis.find(c => c.t === 'texto') ?? visiveis[0];
    const linhas = daqui.map(r => {
      const c = r.c ?? {};
      const titulo = (principal && valorTexto(c[principal.k])) || tituloDe(c) || '—';
      const data = (col.data ? dataTela(c[col.data]) : '') || dataTela(r.criado);
      const det = visiveis.filter(f => f !== principal && f.k !== col.data && valorTexto(c[f.k]))
        .slice(0, 8).map(f => `${t(f.r)}: ${valorTexto(c[f.k])}${f.un ? ' ' + t(f.un) : ''}`).join(' · ');
      return [titulo, data, det];
    });
    return { nome: t(col.nome), linhas };
  }).filter(x => x.linhas.length);
}

/** Monta e compartilha o PDF legível com os dados de todas as abas. */
export async function exportarLegivel() {
  const docs = allDocs() as Record<string, any>;
  const blocos: BlocoPdf[] = [
    { t: 'titulo', texto: t('Meus dados do My Planner') },
    { t: 'sub', texto: t('Arquivo para ler e guardar, organizado por aba. Gerado em {data}.', { data: brYmd(today()) }) },
    { t: 'par', texto: t('Não entram: o conteúdo do Diário e os campos do cofre (ficam protegidos pela senha), fotos e arquivos anexados. Para restaurar os dados no app, use o arquivo técnico.'), tam: 9.5 },
    { t: 'linha' },
  ];
  const ids = [...ABAS.map(a => a.id as string), ...Object.keys(docs).filter(k => !ABAS.some(a => a.id === k))];
  for (const id of ids) {
    if (FORA.has(id) || !docs[id]) continue;
    const listas = (DEFS as Record<string, unknown>)[id] ? listasDasFichas(id as ModId, docs[id]) : listasDoDoc(docs[id]);
    if (!listas.length) continue;
    const nomeAba = ABAS.some(a => a.id === id) ? aba(id as any).nome : id;
    blocos.push({ t: 'espaco', h: 8 }, { t: 'par', texto: nomeAba, negrito: true, tam: 14 });
    for (const l of listas) {
      blocos.push({ t: 'par', texto: `${l.nome} (${l.linhas.length})`, negrito: true, tam: 11 },
        { t: 'tabela', colunas: [t('Item'), t('Data'), t('Detalhes')], linhas: l.linhas, larguras: [0.32, 0.16, 0.52] });
    }
  }
  if (docs.diariop) blocos.push({ t: 'espaco', h: 8 }, { t: 'par', texto: aba('diario').nome, negrito: true, tam: 14 },
    { t: 'par', texto: t('O Diário é protegido pela senha dele e não entra neste arquivo.'), tam: 10 });
  await gerarECompartilharPdf(t('Meus dados - My Planner {data}', { data: today() }), blocos, 'My Planner');
}
